<template>
  <Teleport to="body">
    <Modal
      v-model="modelValue"
      :canClose="true"
      :closeOnEsc="true"
      :closeOnBackdropClick="true"
      :title="title ?? $t('campaigns.choose_image')"
      @update:modelValue="emit('update:modelValue', $event)">
      <template #body>
        <div v-if="images.length === 0" class="text-center p-4">
          {{ $t('campaigns.no_banners_found') }}
        </div>
        <div v-else class="modal-images-scroll">
          <div class="modal-images-grid">
            <AdImagePicker
              v-for="image in images"
              :key="image.id"
              :imageUrl="image.thumbnailUrl"
              :videoUrl="image.videoUrl"
              :overlayMessage="image.overlayMessage"
              :overlayError="image.overlayError"
              :uploadText="uploadText"
              modalSize
              :selected="selectedIds.includes(image.id)"
              @click="!image.disabled && emit('select', image.id)" />
          </div>
        </div>
      </template>

      <template #footer>
        <div class="d-flex justify-content-between w-100 align-items-center">
          <div v-if="showCount" class="text-muted">
            {{ selectedIds.length }} / {{ maxImages }} {{ $t('campaigns.google_ads.images_used') }}
          </div>
          <div v-else />
          <LeadButton variant="secondary" @click="emit('update:modelValue', false)">
            {{ $t('btn.confirm') }}
          </LeadButton>
        </div>
      </template>
    </Modal>
  </Teleport>
</template>

<script setup lang="ts">
import { Modal } from '@prospective/lithium';
import LeadButton from '../generic/LeadButton.vue';
import AdImagePicker from './AdImagePicker.vue';

export interface PickerImage {
  id: number;
  thumbnailUrl?: string | null;
  videoUrl?: string | null;
  overlayMessage?: string | null;
  overlayError?: boolean;
  disabled?: boolean;
}

const modelValue = defineModel<boolean>('modelValue', { required: true, default: false });

withDefaults(
  defineProps<{
    images: PickerImage[];
    selectedIds?: number[];
    maxImages?: number;
    showCount?: boolean;
    uploadText?: string;
    title?: string;
  }>(),
  {
    selectedIds: () => [],
    maxImages: 0,
    showCount: false,
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  select: [id: number];
}>();
</script>

<style scoped lang="scss">
.modal-images-scroll {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 4px;
}

.modal-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
  align-items: center;
}
</style>
